import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SnapTradeReact } from 'snaptrade-react';
import { accountsService } from '../../services/accounts';
import { tradingService } from '../../services/trading';
import { RemoveAccountDialog } from '../holdings/RemoveAccountDialog';

interface Position {
  id: string;
  accountId: string;
  symbol: string;
  quantity: number;
  averageCost: number;
  currentPrice: number;
  marketValue: number;
  unrealizedPnL: number;
  lastUpdatedAt: Date;
}

interface Account {
  id: string;
  accountName: string;
  brokerageName: string;
  balance: number;
}

export function PositionsList() {
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [accountToRemove, setAccountToRemove] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [loginLink, setLoginLink] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();

  // Query to get accounts
  const { data: accounts, isLoading: accountsLoading, refetch: refetchAccounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsService.getAccounts(),
  });

  // Query to get positions for selected account
  const { data: positions, isLoading: positionsLoading, refetch: refetchPositions } = useQuery({
    queryKey: ['positions', selectedAccountId],
    queryFn: () => selectedAccountId ? accountsService.getAccountPositions(selectedAccountId) : Promise.resolve([]),
    enabled: !!selectedAccountId,
  });

  // Mutation to sync all accounts
  const syncMutation = useMutation({
    mutationFn: () => accountsService.syncAllAccounts(),
    onSuccess: (data) => {
      console.log('Sync complete:', data);
      refetchAccounts();
      if (selectedAccountId) {
        refetchPositions();
      }
    },
  });

  // Mutation to refresh specific account
  const refreshAccountMutation = useMutation({
    mutationFn: (accountId: string) => accountsService.refreshAccount(accountId),
    onSuccess: () => {
      refetchAccounts();
      refetchPositions();
    },
  });

  // Mutation to remove account
  const removeMutation = useMutation({
    mutationFn: (accountId: string) => accountsService.removeAccount(accountId),
    onSuccess: (_, removedAccountId) => {
      setAccountToRemove(null);
      // If the removed account was selected, clear selection
      if (selectedAccountId === removedAccountId) {
        setSelectedAccountId(null);
      }
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
    },
    onError: (error) => {
      console.error('Failed to remove account:', error);
      alert('Failed to remove account. Please try again.');
    },
  });

  // Mutation to initiate SnapTrade connection
  const connectMutation = useMutation({
    mutationFn: async () => {
      const result = await tradingService.connectSnapTrade();
      return result;
    },
    onSuccess: (data) => {
      if (data.redirectUrl) {
        setIsConnecting(true);
        setLoginLink(data.redirectUrl);
        setIsModalOpen(true);
      } else {
        setIsConnecting(false);
      }
    },
    onError: (error) => {
      console.error('Connect error:', error);
      setIsConnecting(false);
    },
  });

  const handleConnect = () => {
    connectMutation.mutate();
  };

  const handleSnapTradeSuccess = async (authorizationId: string) => {
    console.log('SnapTrade connection successful!', authorizationId);
    setIsModalOpen(false);
    setLoginLink(null);
    setIsConnecting(false);
    
    // Sync after successful connection
    try {
      const callbackResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8787'}/api/auth/snaptrade-callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      if (callbackResponse.ok) {
        const data = await callbackResponse.json();
        console.log(`Synced ${data.accountsSynced} accounts and ${data.positionsSynced} positions`);
      }

      refetchAccounts();
    } catch (error) {
      console.error('Failed to sync after connection:', error);
    }
  };

  const handleSnapTradeError = (error: any) => {
    console.error('SnapTrade connection error:', error);
    setIsModalOpen(false);
    setLoginLink(null);
    setIsConnecting(false);
  };

  const handleSnapTradeExit = () => {
    console.log('User exited SnapTrade flow');
    setIsModalOpen(false);
    setLoginLink(null);
    setIsConnecting(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setLoginLink(null);
    setIsConnecting(false);
  };

  const handleAccountSelect = (accountId: string) => {
    setSelectedAccountId(accountId);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  if (accountsLoading) {
    return (
      <div className="bg-white dark:bg-dark-card rounded-lg shadow p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-900 dark:text-white">Loading accounts...</span>
        </div>
      </div>
    );
  }

  const accountsList = accounts || [];

  return (
    <div className="space-y-6">
      {/* Account Selection and Sync */}
      <div className="bg-white dark:bg-dark-card rounded-lg shadow p-6">
        <div className="flex items-center justify-end mb-4">
          <div className="flex space-x-2">
            <button
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm disabled:opacity-50"
            >
              {syncMutation.isPending ? 'Syncing...' : 'Sync All Accounts'}
            </button>
            <button
              onClick={handleConnect}
              disabled={connectMutation.isPending || isConnecting}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm disabled:opacity-50"
            >
              {connectMutation.isPending || isConnecting ? 'Connecting...' : 'Connect Account'}
            </button>
          </div>
        </div>

        {accountsList.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400">No accounts found. Connect your brokerage to see positions.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accountsList.map((account: Account) => (
              <div
                key={account.id}
                onClick={() => handleAccountSelect(account.id)}
                className={`border rounded-lg p-4 cursor-pointer transition-colors relative ${
                  selectedAccountId === account.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {/* Remove button in top-right corner */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setAccountToRemove(account.id);
                  }}
                  className="absolute top-2 right-2 p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                  title="Remove account"
                >
                  <svg 
                    className="w-4 h-4" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
                    />
                  </svg>
                </button>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">{account.accountName}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{account.brokerageName}</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                      {formatCurrency(account.balance)}
                    </p>
                  </div>
                  {selectedAccountId === account.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        refreshAccountMutation.mutate(account.id);
                      }}
                      disabled={refreshAccountMutation.isPending}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm"
                    >
                      {refreshAccountMutation.isPending ? 'Refreshing...' : 'Refresh'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Positions Table */}
      {selectedAccountId && (
        <div className="bg-white dark:bg-dark-card rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Positions</h3>
          </div>

          {positionsLoading ? (
            <div className="p-6">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-900 dark:text-white">Loading positions...</span>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Symbol
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Avg Cost
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Current Price
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Market Value
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      P&L
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-dark-card divide-y divide-gray-200 dark:divide-gray-700">
                  {!positions || positions.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        No positions found in this account.
                      </td>
                    </tr>
                  ) : (
                    positions.map((position: Position) => {
                      const plPercent = ((position.unrealizedPnL / (position.averageCost * position.quantity)) * 100);
                      const isProfit = position.unrealizedPnL >= 0;
                      
                      return (
                        <tr key={position.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{position.symbol}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                            {position.quantity.toFixed(4)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                            {formatCurrency(position.averageCost)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                            {formatCurrency(position.currentPrice)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900 dark:text-white">
                            {formatCurrency(position.marketValue)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <div className={isProfit ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                              <div className="font-medium">{formatCurrency(position.unrealizedPnL)}</div>
                              <div className="text-xs">{formatPercent(plPercent)}</div>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Remove Account Dialog */}
      <RemoveAccountDialog
        isOpen={accountToRemove !== null}
        onClose={() => setAccountToRemove(null)}
        onConfirm={() => {
          if (accountToRemove) {
            removeMutation.mutate(accountToRemove);
          }
        }}
        accountName={
          accountToRemove 
            ? accountsList.find((a: Account) => a.id === accountToRemove)?.accountName || 'this account'
            : ''
        }
        isRemoving={removeMutation.isPending}
      />

      {/* SnapTrade SDK iframe integration - only render when we have a loginLink */}
      {loginLink && (
        <SnapTradeReact
          loginLink={loginLink}
          isOpen={isModalOpen}
          close={handleCloseModal}
          onSuccess={handleSnapTradeSuccess}
          onError={handleSnapTradeError}
          onExit={handleSnapTradeExit}
        />
      )}
    </div>
  );
}